import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, CalendarDays, Timer, TrendingUp, Sun, Moon } from "lucide-react";
import { backendApi } from "./api/backendApi";
import { formatDurationFromHours } from "../utils/formatDuration";

const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
};

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const [bg, fg] = s.includes('present') || s.includes('wfh') ? ['#E8F3EC','#2F7D4F']
    : s.includes('late') ? ['#FBF1E1','#B9822E']
      : s.includes('absent') ? ['#FCEAEA','#C0413D']
        : ['#F3F2ED','#6B7280'];
  return <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, background: bg, color: fg, textTransform: 'capitalize' }}>{status || 'present'}</span>;
};

const HeroStat = ({ icon: Icon, label, value, color, tint, full }) => (
  <div style={{
    background: full ? T.green : T.card,
    border: `1.5px solid ${full ? 'transparent' : T.line}`,
    borderRadius: 18, padding: '22px 24px',
    boxShadow: full ? '0 4px 20px rgba(26,60,43,0.2)' : '0 1px 4px rgba(0,0,0,0.04)',
    display: 'flex', flexDirection: 'column', gap: 12,
  }}>
    <div style={{
      width: 42, height: 42, borderRadius: 12,
      background: full ? 'rgba(255,255,255,0.15)' : tint,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={20} color={full ? '#fff' : color} strokeWidth={1.8} />
    </div>
    <div>
      <p style={{ fontSize: 28, fontWeight: 800, color: full ? '#fff' : T.ink, margin: 0, letterSpacing: '-0.02em' }}>{value}</p>
      <p style={{ fontSize: 12, color: full ? 'rgba(255,255,255,0.65)' : T.muted, margin: '4px 0 0', fontWeight: 500 }}>{label}</p>
    </div>
  </div>
);

export default function MyAttendance() {
  const { data: history = [] } = useQuery({
    queryKey: ["my-attendance-history"],
    queryFn: () => backendApi.attendance.history(),
    initialData: [],
  });

  const { data: today } = useQuery({
    queryKey: ["my-attendance-today"],
    queryFn: () => backendApi.attendance.today(),
  });

  const totalHours = history.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
  const daysPresent = history.filter(r => (parseFloat(r.total_hours) || 0) > 0).length;
  const avgHours = daysPresent > 0 ? (totalHours / daysPresent).toFixed(1) : '0';

  const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" }); } catch { return d; } };
  const fmtTime = (t) => { if (!t) return '—'; try { return new Date(t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }); } catch { return t; } };


  const isClockedIn = today?.status === "clocked_in";

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: 28 }}>
      <style>{`
        .ma-row:hover{background:#F7F8F5!important}
        .ma-bar{transition:height 0.6s cubic-bezier(0.16,1,0.3,1)}
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 26 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>My Attendance</h2>
        <p style={{ fontSize: 12.5, color: T.muted, margin: '3px 0 0' }}>Your personal punch-in / punch-out history · last 30 days</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 16, marginBottom: 24 }}>
        <HeroStat icon={isClockedIn ? Sun : Moon} label="Today's Status" value={isClockedIn ? "Clocked In" : "Clocked Out"} color={T.green} tint={T.greenTint} full />
        <HeroStat icon={CalendarDays} label="Days Present (30d)" value={daysPresent} color="#2F7D4F" tint="#E8F3EC" />
        <HeroStat icon={Timer} label="Total Hours (30d)" value={formatDurationFromHours(totalHours)} color="#7E6BE0" tint="#EFEAFA" />
        <HeroStat icon={TrendingUp} label="Avg Hours / Day" value={`${avgHours}h`} color="#B9822E" tint="#FBF1E1" />
      </div>
      
      {/* History table */}
      <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1.5px solid ${T.lineSoft}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} color={T.green} strokeWidth={2} />
          <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>Attendance History</span>
        </div>
        {history.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.lineSoft }}>
                  {['Date','Punch In','Punch Out','Total Hours','Status'].map(h => (
                    <th key={h} style={{ padding: '11px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((r, i) => (
                  <tr key={r.id} className="ma-row" style={{ borderBottom: i < history.length - 1 ? `1px solid ${T.lineSoft}` : 'none', background: T.card }}>
                    <td style={{ padding: '13px 18px', fontSize: 13.5, fontWeight: 600, color: T.ink }}>{fmtDate(r.date)}</td>
                    <td style={{ padding: '13px 18px', fontSize: 13, color: T.body, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(r.punch_in)}</td>
                    <td style={{ padding: '13px 18px', fontSize: 13, color: T.body, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(r.punch_out)}</td>
                    <td style={{ padding: '13px 18px', fontSize: 13.5, fontWeight: 700, color: T.ink }}>
                      {r.total_hours_display || formatDurationFromHours(r.total_hours)}
                    </td>
                    <td style={{ padding: '13px 18px' }}><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Clock size={40} color={T.faint} strokeWidth={1.5} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, margin: '0 0 4px' }}>No records yet</p>
            <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>Punch in from the dashboard to start tracking your hours.</p>
          </div>
        )}
      </div>
    </div>
  );
}